<template>
  <div>
    <v-container class='ma-n3 pa-n3'>
      <v-row no-gutters align='center' justify='space-between' :class="{'collapsed-heading': !expanded, 'secondary': isCurrentVor}" class='mr-n6'>
        <h2
          class="clickable"
          :class="{'collapsed-heading': !expanded, 'secondary': isCurrentVor}"
          @click="expanded=!expanded"
        >
          <v-icon v-if="isCurrentVor" small class="mx-2">warning</v-icon>
          Vehicle Off Road
          <v-icon v-if="expanded">arrow_drop_up</v-icon>
          <v-icon v-else>arrow_drop_down</v-icon>
        </h2>
        <v-menu offset-y>
          <template v-slot:activator='{ on }'>
            <v-btn small tile v-on='on' class='mr-1'>
              <v-icon>save_alt</v-icon>&nbsp;Export
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click='exportCsv()'>
              <v-list-item-title>
                <v-icon>grid_on</v-icon>&nbsp;Excel (CSV)
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-row>
    </v-container>


    <v-expand-transition>
      <v-card fill-height v-show="expanded">
        <v-data-table
          :items="items"
          :items-per-page="options.itemsPerPage"
          :headers="headers"
          :loading="$wait.is(['loading vor history'])"
          :footer-props.sync="footerProps"
          :options.sync="options"
          :sort-by='options.sortBy'
          :sort-desc='options.sortDesc'
          :multi-sort=true
        >
          <v-progress-linear v-slot:progress color="blue" indeterminate></v-progress-linear>
          <template v-slot:item="items">
            <tr>
              <td>{{ items.item.startDate | formatDate }}</td>
              <td>{{ items.item.actualEndDate | formatDate }}</td>
              <td>{{ items.item.duration }}</td>
              <td>{{ items.item.reason }}</td>
              <td>{{ items.item.category }}</td>
              <td>{{ items.item.returnedToService ? 'Yes' : 'No' }}</td>
              <td v-if="!disabled">
                <v-btn @click="openEdit(items.item)" icon small>
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn @click="confirmDelete(items.item)" icon small color="red">
                  <v-icon>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
          <v-card-actions v-if="!disabled">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="openAdd()">Add VOR</v-btn>
            <v-dialog v-model="dialog" max-width="500px" persistent>
              <Form
                ref='form'
                v-model="model"
                @add="addVor($event.id, $event.model)"
                @edit="editVor($event.id, $event.model)"
                @close="dialog = false"
              >
                <template v-slot:header>
                  <v-spacer></v-spacer>
                  <v-btn @click="dialog = false" icon text>
                    <v-icon>close</v-icon>
                  </v-btn>
                </template>
              </Form>
            </v-dialog>
          </v-card-actions>
      </v-card>
      </v-expand-transition>
  </div>
</template>

<script>
  import Form from './Form.vue';
  import VehicleOffRoad from '@/components/Units/VehicleOffRoadClient.vue';
  import GarageLocationsClient from '@/components/GarageLocations/GarageLocationsClient.vue';
  import {Event} from '@/plugins/bus';
  import moment from 'moment';
  import { DateFormat } from '@/plugins/date-filter';
  import DownloadHelper from '@/lib/DownloadHelper';

  export default {
    components: {
      Form
    },
    props: {
      unit: {
        required: true,
      }
    },
    mixins: [VehicleOffRoad, GarageLocationsClient],
    data() {
      return {
        dialog: false,
        expanded: false,
        items: [],
        model: {},
        isCurrentVor: false,
        options: {
          itemsPerPage: 10,
          sortBy: ['returnedToService', 'actualEndDate'],
          sortDesc: [false, true]
        },
        footerProps: {
          itemsPerPageOptions: [10, 50, 100]
        },
        headers: [
          {text: 'Start Date', value: 'startDate', sortable: true},
          {text: 'End Date', value: 'actualEndDate', sortable: true},
          {text: 'Duration', value: 'duration', sortable: true},
          {text: 'Reason', value: 'reason', sortable: true},
          {text: 'Category', value: 'category', sortable: true},
          {text: 'Returned To Service', value: 'returnedToService', sortable: true},
          {text: '', value: 'actions', sortable: false}
        ]
      };
    },
    watch: {
      dialog() {
        this.$refs.form.resetValidation();
      }
    },
    computed: {
      params() {
        return {
          pageSize: this.options && this.options.itemsPerPage,
          page: this.options && this.options.page,
          unitId: this.unitId
        };
      },
      disabled() {
        const g = this.unit.garageLocationId;
        return this.unit.isDeleted || (!this.userIsWorkshopManagerInGarage(g, this.userGarages) && !this.userIsFleetManager);
      },
      unitId() {
        return this.$route.params.unitId;
      }
    },
    methods: {
      openAdd() {
        this.model = {
          unitId: this.unitId
        };
        this.dialog = true;
      },
      openEdit(vehicleOffRoad) {
        this.model = {
          ...vehicleOffRoad, // spread operator intentionally used to create clone
          unitId: this.unitId
        };
        this.dialog = true;
      },
      confirmDelete(vehicleOffRoad) {
        this.$confirmCancel.show(`Delete VOR?`, `<p>This will permanently delete this VOR</p>`, 'Delete it');
        this.$confirmCancel.$on('confirm-cancel', confirmed => {
          if (!confirmed) {
            return;
          }
          this.$bus.$emit(Event.VehicleOffRoadPendingDelete, {
            id: vehicleOffRoad.id,
            unitId: this.unitId
          });
        });
      },
      async addVor(id, model) {
        this.dialog = false;
        await this.addVehicleOffRoad(model.unitId, id, model);
      },
      async editVor(id, model) {
        this.dialog = false;
        await this.updateVehicleOffRoad(model.unitId, model);
      },
      async handleVehicleOffRoadDeleted() {
        await this.performGetVor();
      },
      async handleVehicleOffRoadUpdated() {
        await this.performGetVor();
      },
      closeDialog() {
        this.dialog = false;
      },
      async performGetVor() {
        this.isCurrentVor = false;
        this.items = await this.getVehicleOffRoadHistory(this.unitId);

        const now = new Date();
        for (let item of this.items) {
          if (moment(item.startDate) <= now && !item.returnedToService) {
            this.isCurrentVor = true;
            break;
          }
        }
      },
      async exportCsv() {
        let orderBy = this.options.sortBy.map( (e, i) => {
          if (this.options.sortDesc && this.options.sortDesc.length && this.options.sortDesc[i]) {
            return `-${e}`;
          } else {
            return e;
          }
        } );
        const results = await this.getVehicleOffRoadHistoryForReport(this.unitId, {orderBy});
        const data = results ? results : [];

        const rows = data.map(x => [
          x.unitNumber,
          x.startDate,
          x.endDate,
          x.returnedToServiceDate,
          x.duration,
          x.reason,
          x.category,
          x.returnedToService
        ]);

        const csv = DownloadHelper.makeCsv(
          [
            'Unit',
            'First day',
            'Expected date back',
            'Actual return to service',
            'VOR Duration - First day to Actual day',
            'VOR Reason',
            'VOR Category',
            'Returned to service flag'
          ],
          rows
        );
        DownloadHelper.download(`${this.unit.number}_vor_${moment().format(DateFormat.Canonical)}.csv`, new Blob([csv]));
      },
    },
    async mounted() {
      await this.performGetVor();
      this.$bus.$on(Event.VehicleOffRoadPendingAdd, this.closeDialog);
      this.$bus.$on(Event.VehicleOffRoadPendingUpdate, this.closeDialog);
      this.$bus.$on(Event.VehicleOffRoadDeleted, this.handleVehicleOffRoadDeleted);
      this.$bus.$on(Event.VehicleOffRoadUpdated, this.handleVehicleOffRoadUpdated);
    },
    destroyed() {
      this.$bus.$off(Event.VehicleOffRoadPendingAdd, this.closeDialog);
      this.$bus.$off(Event.VehicleOffRoadPendingUpdate, this.closeDialog);
      this.$bus.$off(Event.VehicleOffRoadDeleted, this.handleVehicleOffRoadDeleted);
      this.$bus.$off(Event.VehicleOffRoadUpdated, this.handleVehicleOffRoadUpdated);
    }
  };
</script>
